import classNames from 'classnames';
import React, {Dispatch, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {
  CheckSmallIcon,
  Icon,
  Link,
  MatchesMediaQuery,
  Theme,
  ThemeName,
} from '@taopix/taopix-design-system';
import {useUiState} from '../Context/UserInterface/UserInterfaceContext';
import {AssetsMoreOptions} from './Components/AssetsMoreOptions';
import {useAssetHelperService} from '../../Hooks/Assets/useAssetHelperService';
import useStatusChecker from '../../Hooks/Assets/useStatusChecker';
import {useTaopix} from '../Context/TaopixContext';
import {AssetMetaInterface} from '@taopix/taopix-core/dist/types/types';
import {UserInterfaceActions} from '../Context/UserInterface/UserInterfaceActionTypes';
import {CoreActions} from '@taopix/taopix-core';
import {UserFeedbackActions} from '../Context/UserFeedback/UserFeedbackActionTypes';
import {useDispatch} from 'react-redux';
import {ActionType} from 'typesafe-actions';
import {useUserFeedback} from '../Context/UserFeedback/UserFeedbackContext';
import AddPicturesButton from '../AssetServices/AddPicturesButton';

type AssistantSubHeaderProps = {
  assetCount?: number;
  coverAssetsCount?: number;
  maxCoverAssetsCount?: number;
  minSelection?: number;
  selectedAssetsCount?: number;
  assetsWithError?: AssetMetaInterface[];
  showAddPicturesButton?: boolean;
};

export const AssistantSubHeader = ({
  assetCount = 0,
  minSelection = 0,
  coverAssetsCount,
  maxCoverAssetsCount = 0,
  selectedAssetsCount,
  assetsWithError = [],
  showAddPicturesButton = true,
}: AssistantSubHeaderProps) => {
  const app = useTaopix();
  const assetHelper = useAssetHelperService(app.core.get('server'));
  const monitor = useStatusChecker();
  const userFeedback = useUserFeedback();
  const ui = useUiState();
  const {t} = useTranslation(['assistants']);
  const dispatch = useDispatch<Dispatch<ActionType<typeof CoreActions>>>();
  const verySmallScreen = MatchesMediaQuery('(max-height:400px)');

  const onRemoveFailedAssets = React.useCallback(() => {
    if (!monitor) return;

    ui.dispatch(UserInterfaceActions.setIsBusy(true));
    assetHelper
      .onDeleteAssets(assetsWithError)
      .then(response => {
        response.success.forEach(assetID => {
          dispatch(CoreActions.deleteAsset(assetID));
          if (monitor.includes(assetID)) {
            monitor.remove(assetID);
          }
        });

        if (response.failed.length > 0) {
          userFeedback.dispatch(
            UserFeedbackActions.setInfoUserFeedback({
              title: '',
              message: t('*:str_ErrorNotDeleted'),
              showHeader: true,
            })
          );
        }
      })
      .catch(error => {
        userFeedback.dispatch(
          UserFeedbackActions.setErrorUserFeedback({
            title: '',
            message: error.message,
            showHeader: true,
          })
        );
      })
      .finally(() => {
        ui.dispatch(UserInterfaceActions.setIsBusy(false));
      });
  }, [monitor, assetsWithError]);

  const assetCountMessage = (() => {
    let label = '';
    label =
      1 === assetCount
        ? '*:str_LabelLayoutNumberPictureSingle'
        : '*:str_LabelLayoutNumberPicturePlural';

    return t(label).replace('^0', String(assetCount));
    // }
  })();

  const coverCountMessage = (() => {
    const label =
      1 === maxCoverAssetsCount
        ? 'str_MessagePicturesSelectedCoverPicturesSingular'
        : 'str_MessagePicturesSelectedCoverPicturesPlural';

    return t(label)
      .replace('^0', String(coverAssetsCount))
      .replace('^1', String(maxCoverAssetsCount));
  })();

  /**
   * Smart design label.
   */
  const selectedAssetsCountMessage = useMemo(() => {
    const label = 'str_MessagePicturesWillBeUsedinYourProject';

    return t(label).replace('^0', String(selectedAssetsCount));
  }, [selectedAssetsCount]);

  const subHeaderClassNames = classNames(
    'flex',
    'items-center',
    'flex-wrap',
    'py-md',
    'px-lg',
    'z-[1]', // The z-index ensures that the shadow will appear above the media gallery thumbnails
    'shadow-[0_5px_5px_rgba(0,0,0,0.1)]'
  );

  const getUsageTheme = (minRequired: number, selectionCount: number): string => {
    return selectionCount >= minRequired ? ThemeName.Information : ThemeName.Warning;
  };

  const [canContinue, setCanContinue] = useState(false);

  useEffect(() => {
    if (coverAssetsCount && coverAssetsCount >= minSelection) {
      setCanContinue(true);
    } else if (canContinue) {
      setCanContinue(false);
    }
  }, [coverAssetsCount]);

  const assetsWithErrorsCount = (assetsWithError || []).length;

  const assetCountPrompts = [
    assetsWithErrorsCount > 0 && (
      <Theme
        name={ThemeName.Warning}
        element="p"
        allowBorder={false}
        className={'py-xs px-sm lg:max-w-[400rem]'}
      >
        {t('assistants:str_MessageSomePicturesFailedToLoad')}{' '}
        <Link onClick={onRemoveFailedAssets}>
          {t('assistants:str_ButtonRemoveAllFailedPictures')}
        </Link>
      </Theme>
    ),
    0 === assetsWithErrorsCount &&
      assetCount > 0 &&
      minSelection > 0 &&
      minSelection - assetCount > 0 && (
        <Theme
          element="span"
          name={getUsageTheme(minSelection, assetCount)}
          allowBorder={false}
          className={'flex py-xs px-sm justify-center'}
          key={'assistant-sub-header-prompt-add-pictures'}
        >
          {t('*:str_MessageAddMorePicturesPlural').replace('^0', String(minSelection - assetCount))}
        </Theme>
      ),
    0 === assetsWithErrorsCount && undefined !== coverAssetsCount && maxCoverAssetsCount > 0 && (
      <Theme
        element="span"
        name={canContinue ? ThemeName.Information : ThemeName.Warning}
        allowBorder={false}
        className={'flex py-xs px-sm justify-center'}
        key={'assistant-sub-header-prompt-cover-count'}
      >
        {canContinue && <Icon size={'small'} icon={<CheckSmallIcon />} className={'self-start'} />}
        {coverCountMessage}
      </Theme>
    ),
    0 === assetsWithErrorsCount && undefined !== selectedAssetsCount && (
      <Theme
        element="span"
        name={getUsageTheme(minSelection, selectedAssetsCount)}
        allowBorder={false}
        className={'flex py-xs px-sm justify-center'}
        key={'assistant-sub-header-prompt-selected-pictures-count'}
      >
        {selectedAssetsCountMessage}
      </Theme>
    ),
  ].filter(Boolean);

  return (
    <div id={'assistant-sub-header'} className={subHeaderClassNames}>
      <div className={'flex-1 flex items-center sm:text-sm'}>{assetCountMessage}</div>
      <div className={'px-sm flex'}>
        {showAddPicturesButton && (
          <>
            <AddPicturesButton
              buttonProps={{
                buttonStyle: 'primary',
                label:
                  assetCount > 0 ? t('*:str_ButtonAddMorePictures') : t('*:str_ButtonAddPictures'),
                hideLabel: verySmallScreen,
                startIcon: undefined,
              }}
              popoutProps={{
                className: 'sm:min-w-[min(theme(width.bounds),300rem)]',
                popDirection: ui.state.isSmallScreen ? 'up' : 'down',
              }}
            />
            {!ui.state.isSmallScreen && (
              <AssetsMoreOptions monitor={monitor} assetHelper={assetHelper} />
            )}
          </>
        )}
      </div>
      <div className={'flex-1 flex space-x-sm justify-end'}>
        {ui.state.isSmallScreen ? (
          showAddPicturesButton && <AssetsMoreOptions monitor={monitor} assetHelper={assetHelper} />
        ) : (
          <>{assetCountPrompts}</>
        )}
      </div>
      {ui.state.isSmallScreen && assetCountPrompts.length > 0 && (
        <div className={'flex-col w-full mt-sm'}>{assetCountPrompts}</div>
      )}
    </div>
  );
};
